import { cn } from '@repo/tailwind-config/tailwind.functions'
import { useQuery } from '@tanstack/react-query'

import { getCenterBudget } from '../../../../api/centers/get-center-budget'
import {
  getCountriesList,
  getCountriesListQueryKey
} from '../../../../api/countries/get-countries-list'
import useAuth from '../../../../lib/hooks/use-auth'
import useTranslate from '../../../../lib/hooks/use-translate'
import { CurrencyEnum } from '../../../../lib/types/enums/common/currency.enum'
import { JourneyBudget } from '../../../../lib/types/models/center/center-budget'
import { IdInput } from '../../../../lib/types/utils'
import EditableTable from '../../../elements/editable-table'
import {
  centerCostsTableColorMapping,
  CenterCostsTableFooter,
  CenterTableSkeleton
} from './center.ui'
import { getCenterDetailedActivitiesColumns } from './utils'

type Props = {
  centerId: number
  currency: CurrencyEnum
  costType: 'original-cost' | 'final-cost'
}

export function CenterDetailedActivitiesTable({ centerId, currency, costType }: Props) {
  const { t } = useTranslate()
  const { user } = useAuth()

  const { data: centerBudget } = useQuery({
    queryKey: ['center-budget', centerId],
    queryFn: () => getCenterBudget(centerId)
  })

  const { data: countryList } = useQuery({
    queryKey: getCountriesListQueryKey(),
    queryFn: () => getCountriesList(),
    staleTime: Infinity
  })

  if (!centerBudget || !user) return <CenterTableSkeleton />

  return (
    <EditableTable<IdInput<JourneyBudget>>
      overflow={false}
      isEditable={false}
      data={centerBudget.journeysBudget.map((journeyBudget) => ({
        id: journeyBudget.journey.id,
        ...journeyBudget
      }))}
      columns={getCenterDetailedActivitiesColumns(costType, t, countryList?.data)}
      manualSorting={false}
      footerRowClassName={cn({ 'bg-white': costType === 'final-cost' })}
      footer={
        <CenterCostsTableFooter
          currency={currency}
          costType={'original-cost'}
          textColor={centerCostsTableColorMapping[user.role][costType].textColor}
          variant={centerCostsTableColorMapping[user.role][costType].variant}
          label={t('center.costs.detailed.activities.table.total.budget.of.the.center')}
          total={centerBudget.totalBudget}
        />
      }
    />
  )
}
